export type MetroConversationStateId =
  | 'greeting'
  | 'review_current_role'
  | 'assess_skills'
  | 'identify_gaps'
  | 'set_goals'
  | 'course_search_results'
  | 'create_action_plan'
  | 'close';

export type MetroConversationState = {
  id: MetroConversationStateId;
  description: string;
  instructions: string[];
  examples: string[];
  transitions: Array<{ next_state: MetroConversationStateId; condition: string }>;
};

/**
 * Maps each state to a progress bar index.
 * -1 means the progress bar is hidden (greeting / close).
 * 0–4 map to: Rolle, Skills, Gaps, Ziele, Aktionsplan.
 */
export const METRO_PHASE_INDEX: Record<MetroConversationStateId, number> = {
  greeting: -1,
  review_current_role: 0,
  assess_skills: 1,
  identify_gaps: 2,
  set_goals: 3,
  course_search_results: 3,
  create_action_plan: 4,
  close: -1
};

export const METRO_CONVERSATION_STATES: MetroConversationState[] = [
  {
    id: 'greeting',
    description: 'Warm welcome; get the user ready; then present the plan and confirm.',
    instructions: [
      'Your very first message is the fixed welcome greeting — say it exactly as given in the WELCOME_MESSAGE.',
      'After the user gives any first confirmation ("ja", "klar", "okay", "los"), call reveal_progress_bar FIRST, then explain the session structure ONCE: "Ich schlage vor, wir gehen durch Rolle, Skills, Gaps, Ziele, und definieren dann zusammen deinen Aktionsplan. Passt das für dich?" — do NOT repeat this.',
      'Only after the user confirms AGAIN (second time) call set_conversation_state("review_current_role").',
      'IMPORTANT — Front-loaded information: If Daniel\'s first response already contains substantive information about his role, skills, OR skill gaps (e.g. "Ich bin Account Manager und kann gut verhandeln, aber mir fehlt Value-Based Selling"), do NOT ignore it. Call reveal_progress_bar, then IMMEDIATELY acknowledge the information in a single combined sentence ("Du hast ja schon vieles mitgebracht — du bist [role], stark in [skills], und möchtest an [gap] arbeiten. Stimmt das so?"), then call set_conversation_state to the deepest state the information covers: if role + skills + gaps are all present, call set_conversation_state("set_goals"); if only role is given, call set_conversation_state("assess_skills"); if role + skills are given, call set_conversation_state("identify_gaps"). Do NOT repeat the plan structure explanation in this case. CRITICAL: After the user confirms ("Yeah", "ja", etc.), immediately treat the mentioned gap as the first goal — call add_goal_card with a short German label for it, then call search_courses. Do NOT ask "Was möchtest du erreichen?" — the user already answered that.',
      'If Daniel explicitly asks to skip states (e.g. "geh direkt zu den Zielen"), call reveal_progress_bar if not yet done, then immediately call set_conversation_state to the requested state — no second confirmation needed.'
    ],
    examples: [
      'Hi Daniel, schön, dass du da bist. Ich unterstütze dich dabei, passende Weiterbildungen für dich zu entdecken. Wollen wir loslegen?',
      'Ich schlage vor, wir gehen durch Rolle, Skills, Gaps, Ziele, und definieren dann zusammen deinen Aktionsplan. Passt das für dich?'
    ],
    transitions: [
      {
        next_state: 'review_current_role',
        condition: 'User confirms the session plan for the second time.'
      }
    ]
  },
  {
    id: 'review_current_role',
    description: 'Summarize Daniel\'s current role from the CV and confirm accuracy.',
    instructions: [
      'Briefly summarize Daniel\'s current position, key responsibilities, and one notable project (e.g., DataDrive 2023 or Kaufland Deal) — draw directly from the CV.',
      'End with: "Ist das soweit richtig, oder möchtest du noch etwas ergänzen?"',
      'After acknowledgement, call set_conversation_state("assess_skills").'
    ],
    examples: [
      'Du bist Account Manager für Digital Solutions bei Metro und betreust eigenständig 18 Mid-Market-Accounts. Zuletzt hast du beim Kaufland Enterprise Deal mitgearbeitet und den DataDrive-Upsell mit 140 % Zielerreichung abgeschlossen. Ist das soweit richtig, oder möchtest du noch etwas ergänzen?'
    ],
    transitions: [
      {
        next_state: 'assess_skills',
        condition: 'Daniel confirms or adjusts the role summary.'
      }
    ]
  },
  {
    id: 'assess_skills',
    description: 'Review Daniel\'s strengths and where he already feels confident.',
    instructions: [
      'Reference the completed eLearning courses and the skill matrix from the CV.',
      'Highlight 2 clear strengths (e.g., CRM usage, structured communication, cross-sell success).',
      'Ask: "Wo fühlst du dich am sichersten — gibt es Bereiche, in denen du schon wirklich gut bist?"',
      'After the user responds, call set_conversation_state("identify_gaps").'
    ],
    examples: [
      'Aus deiner Lernhistorie sehe ich, dass du bei CRM-Nutzung und digitaler Kommunikation top bewertet wurdest — und der DataDrive-Erfolg zeigt, dass Cross-Selling gut funktioniert. Wo fühlst du dich am sichersten?'
    ],
    transitions: [
      {
        next_state: 'identify_gaps',
        condition: 'Daniel has reflected on his strengths.'
      }
    ]
  },
  {
    id: 'identify_gaps',
    description: 'Discuss the skill gaps revealed by the assessment.',
    instructions: [
      'Name the top 2–3 gaps concretely from the CV (e.g., Value-Based Selling, C-Level Verhandlung, Enterprise Account Planung).',
      'Ask: "Siehst du das ähnlich — wo siehst du selbst das größte Entwicklungspotenzial?"',
      'After agreement, call set_conversation_state("set_goals").'
    ],
    examples: [
      'Wenn ich mir deine bisherigen Erfahrungen anschaue, sehe ich die größten Entwicklungsfelder bei Value-Based Selling, Verhandlung auf C-Level und Enterprise Account Planung. Siehst du das ähnlich?'
    ],
    transitions: [
      {
        next_state: 'set_goals',
        condition: 'Gaps are acknowledged and priorities are agreed.'
      }
    ]
  },
  {
    id: 'set_goals',
    description: 'Collect 1–4 goals with Daniel, display each as a card, then search courses per goal sequentially.',
    instructions: [
      'CRITICAL: If a skill gap or goal was already mentioned earlier in the conversation (e.g. "mir fehlt Value-Based Selling"), treat it IMMEDIATELY as the first goal — call add_goal_card right away and do NOT ask "Was möchtest du...?" again. Asking again wastes the user\'s time and ignores what they already said.',
      'Only ask "Was möchtest du in den nächsten 12 Monaten erreichen — wo willst du dich am stärksten weiterentwickeln?" if no goal or gap has been mentioned yet.',
      'Each time Daniel names a goal, immediately call add_goal_card(goalId, title) with a short German label.',
      'If Daniel has already signaled he has only one goal (e.g. "nur aus einem Grund", "nur dieses", "genau das"), do NOT ask for more goals — go directly to the confirmation step.',
      'Continue collecting goals only if Daniel seems open to adding more. Ask at most once: "Gibt es noch ein weiteres Ziel?"',
      'Once goals are collected, ask: "Wenn das deine wichtigsten Ziele sind — sollen wir jetzt passende Kurse dazu suchen?"',
      'After confirmation, call search_courses for the FIRST goal (goalIndex: 0) with a targeted query.',
      'WAIT — do NOT call select_course on your own. The user picks a course by clicking the card or saying "ich wähle Kurs X". You will then receive "Ich wähle diesen Kurs: [title]." as confirmation.',
      'After receiving the confirmation for goal N, acknowledge it briefly, then immediately call search_courses for goal N+1 (if it exists).',
      'Count the goals: if there are 3 goals, you must receive 3 separate "Ich wähle diesen Kurs:" messages before calling set_conversation_state("create_action_plan").',
      'NEVER call set_conversation_state("create_action_plan") until ALL goals have a confirmed course.'
    ],
    examples: [
      'Was möchtest du in den nächsten 12 Monaten erreichen — wo willst du dich am stärksten weiterentwickeln?',
      'Super, Strategic Account Planning ist ein klares Ziel. Gibt es noch etwas, das du angehen möchtest?',
      'Wenn das deine wichtigsten Ziele sind — sollen wir jetzt passende Kurse dazu suchen?',
      'Schau mal auf die Karten — welcher Kurs spricht dich am meisten an?',
      'Gute Wahl! Dann schauen wir jetzt Kurse für dein zweites Ziel an.'
    ],
    transitions: [
      {
        next_state: 'create_action_plan',
        condition: 'Goals are confirmed and Daniel is satisfied with the course selection.'
      }
    ]
  },
  {
    id: 'course_search_results',
    description: 'Present course results and let Daniel choose or refine.',
    instructions: [
      'Do NOT read out full course titles or descriptions — the cards on screen already show that.',
      'Give a brief, energizing spoken overview: "Hier sind drei top Optionen — schau mal auf die Karten!"',
      'Ask which one appeals to Daniel.',
      'If Daniel wants something different, call search_courses again with a refined query.',
      'WAIT for Daniel to pick a course — do NOT call select_course on your own.',
      'If Daniel picks by voice, call select_course(courseId). If more goals remain without a course, search their courses next before calling set_conversation_state("create_action_plan").',
      'Only call set_conversation_state("create_action_plan") when ALL goals have a selected course.'
    ],
    examples: [
      'Ich habe drei starke Kurse gefunden, die genau auf deine Lücken einzahlen. Welcher klingt am interessantesten für dich?'
    ],
    transitions: [
      {
        next_state: 'create_action_plan',
        condition: 'Daniel confirms courses for ALL goals.'
      },
      {
        next_state: 'set_goals',
        condition: 'Daniel wants to refine the search with different criteria.'
      }
    ]
  },
  {
    id: 'create_action_plan',
    description: 'Summarize the full plan: goals, course sequence, and practice activities.',
    instructions: [
      'Before summarizing, check: do all goals already have a course selected? If any goal is still missing a course, call search_courses for that goal first (using its goalIndex) — do not summarize until all goals have courses.',
      'Summarize the agreed goals and the course(s) selected in a clear, spoken action plan.',
      'Add one concrete practice element (e.g., a monthly deal review or a pitch practice session with the manager).',
      'If Daniel wants to swap a COURSE for an existing goal, call search_courses again with updated criteria (same goalIndex).',
      'If Daniel wants to REPLACE A GOAL entirely: first call add_goal_card with the SAME goalId as the goal being replaced and the new title (this clears the old course selection and updates the card). Then call search_courses with the same goalIndex and new goalTitle. Do NOT use a new goalId.',
      'Close with: "Haben wir alles beisammen — sollen wir den Plan so festhalten?"',
      'After final confirmation, you MUST call set_conversation_state("close") — every time, including if this is a second or third visit to this state.'
    ],
    examples: [
      'Zusammenfassung: Wir starten mit Value-Based Selling, dann Negotiation Mastery, danach Enterprise Account Management. Dazu vereinbaren wir monatlich einen Deal-Review mit Sarah. Sollen wir das so festhalten?'
    ],
    transitions: [
      {
        next_state: 'close',
        condition: 'Action plan is confirmed by Daniel.'
      }
    ]
  },
  {
    id: 'close',
    description: 'Close the session with encouragement and a clear next step.',
    instructions: [
      'Congratulate Daniel on the clear development plan.',
      'Mention one concrete next step (e.g., enrolling in the first course this week).',
      'Close warmly and briefly.',
      'If Daniel then asks for more courses, additional adjustments, or wants to change something, call set_conversation_state("create_action_plan") to re-enter that state — do NOT close again without calling the tool.'
    ],
    examples: [
      'Großartig, Daniel — du hast heute einen echten Plan für deinen nächsten Karriereschritt gemacht. Meld dich einfach, wenn du etwas anpassen möchtest. Viel Erfolg!'
    ],
    transitions: []
  }
];

export const METRO_CONVERSATION_STATE_ORDER: MetroConversationStateId[] =
  METRO_CONVERSATION_STATES.map(state => state.id);

export const INITIAL_METRO_STATE: MetroConversationStateId = 'greeting';

let currentMetroStateId: MetroConversationStateId = INITIAL_METRO_STATE;

export function getCurrentMetroStateId() {
  return currentMetroStateId;
}

export function setCurrentMetroStateId(stateId: MetroConversationStateId) {
  currentMetroStateId = stateId;
}

export function getMetroStateById(stateId: MetroConversationStateId) {
  return METRO_CONVERSATION_STATES.find(state => state.id === stateId);
}

export function getMetroNextStateIds(stateId: MetroConversationStateId) {
  const state = getMetroStateById(stateId);
  return state ? state.transitions.map(t => t.next_state) : [];
}

export function buildMetroStateSection(stateId: MetroConversationStateId) {
  const state = getMetroStateById(stateId);
  if (!state) {
    return `Current state: ${stateId} (unknown).`;
  }

  const instructionLines = state.instructions.map(line => `- ${line}`).join('\n');
  const exampleLines = state.examples.map(line => `- ${line}`).join('\n');
  const transitionLines = state.transitions
    .map(t => `- ${t.next_state}: ${t.condition}`)
    .join('\n');

  return [
    `# Current State: ${state.id}`,
    state.description,
    '',
    '## Instructions',
    instructionLines,
    '',
    '## Sample Phrases (vary responses, never repeat the same phrase twice)',
    exampleLines,
    '',
    '## Allowed Transitions',
    transitionLines || '- None (terminal state)',
    '',
    `When the exit condition is met, call set_conversation_state with the next_state.`
  ].join('\n');
}
